/*
  PN-057 — wspólna logika karty produktu (snippets/product-item.liquid).

  Swiper ładowany jest NA ŻĄDANIE — dopiero gdy jakaś galeria zbliża się do viewportu.
  Wcześniej każda karta z >1 zdjęciem wstawiała własny <script src="swiper-bundle.min.js">
  (101 tagów na stronie głównej; przeglądarka deduplikuje pobranie, ale nie parsowanie tagów).

  Dla treści wstrzykiwanej AJAX-em (templates/collection.ajax.liquid) wołaj:
    window.pianoProductItem.init(kontener)
  Skrypty w innerHTML się nie wykonują, więc inline'y i tak nigdy tam nie działały.

  ZASADA NADRZĘDNA — ten plik nie trzyma referencji do węzłów DOM.
  Stan „już obsłużone" siedzi w atrybutach na samych elementach, a obserwator jest JEDEN,
  współdzielony. Dzięki temu podmiana `innerHTML` (kolekcja, filtry AJAX) nie zostawia
  retainerów. Obiekty poniżej są singletonami na całą stronę — świadomie NIE ma tu
  obiektu na kartę, bo to wróciłoby dokładnie do wycieku, który PN-057 usunęło
  (listenery 151 953 → 874 na stronie głównej).
*/
(function () {
	'use strict';

	/* ---------- Stałe ---------- */

	const GALLERY_SELECTOR = '.swiper[class*="product-swiper-"]';
	// Posters are revealed/hidden exclusively by the video controller.
	const LAZY_IMAGE_SELECTOR = 'img[loading="lazy"]:not(.pn-video__poster)';

	const GALLERY_BOUND_ATTR = 'data-piano-gallery-bound';
	const FADE_BOUND_ATTR = 'data-piano-fade-bound';
	const SWIPER_SCRIPT_ATTR = 'data-piano-swiper';

	// PN-036: ~900 px w poziomie (≈2 slajdy karuzeli do przodu), 200 px w pionie
	// (zapas dla siatek). Wartość sprawdzona — nie zmieniać bez ponownego pomiaru.
	const GALLERY_ROOT_MARGIN = '200px 900px';

	/* ---------- Helpery ---------- */

	/**
	 * Oznacza element atrybutem-strażnikiem. Zwraca false, jeśli był już oznaczony.
	 * Zastępuje dwa identyczne wzorce `if (hasAttribute) continue; setAttribute(...)`.
	 * @returns {boolean} true, jeśli to pierwsze oznaczenie tego elementu
	 */
	function markOnce(element, attribute) {
		if (element.hasAttribute(attribute)) return false;
		element.setAttribute(attribute, '');
		return true;
	}

	/**
	 * Bezpieczne przejście po elementach w zakresie. `root` bywa dowolnym węzłem
	 * przekazanym z zewnątrz (AJAX), więc sprawdzamy, czy w ogóle umie szukać.
	 */
	function eachElement(root, selector, callback) {
		if (!root || typeof root.querySelectorAll !== 'function') return;
		for (const element of root.querySelectorAll(selector)) callback(element);
	}

	/** Pasek paginacji galerii — jeden segment na zdjęcie, równej szerokości. */
	function renderPaginationSegments(current, total) {
		let html = '';
		for (let i = 0; i < total; i++) {
			const isActive = i + 1 === current ? 'active' : '';
			html +=
				`<span class="pagination-segment ${isActive}"` +
				` style="width: calc(100% / ${total})"` +
				` data-index="${i}"></span>`;
		}
		return html;
	}

	/**
	 * Konfiguracja galerii karty — zachowanie 1:1 z PN-036.
	 * Świeży obiekt na każde wywołanie, bo Swiper trzyma i modyfikuje własne `params`.
	 */
	function createGalleryOptions() {
		return {
			direction: 'horizontal',
			loop: false,
			slidesPerView: 1,
			touchRatio: 1,
			nested: true,
			observer: true,
			observeParents: true,
			lazyPreloadPrevNext: 1,
			lazyPreloaderClass: 'swiper-custom-preloader',
			pagination: {
				el: '.swiper-pagination',
				type: 'custom',
				renderCustom: (swiper, current, total) => renderPaginationSegments(current, total),
				clickable: true,
			},
			touchEventsTarget: 'container',
		};
	}

	/* ---------- Ładowanie Swipera na żądanie ---------- */

	/**
	 * Pobiera bundle Swipera dokładnie raz i kolejkuje zadania, które go potrzebują.
	 * URL wstrzykuje Liquid do `window.pianoSwiperSrc` (layout/theme.liquid).
	 */
	class SwiperLoader {
		/** @type {Array<() => void>} */
		#pending = [];
		/** @type {'idle' | 'loading' | 'ready' | 'failed'} */
		#state = 'idle';

		get isReady() {
			return typeof Swiper !== 'undefined';
		}

		/** Uruchamia zadanie od razu, jeśli Swiper już jest — inaczej kolejkuje i pobiera. */
		whenReady(task) {
			if (this.isReady) {
				task();
				return;
			}
			if (this.#state === 'failed') return;

			this.#pending.push(task);
			this.#startLoading();
		}

		#startLoading() {
			if (this.#state === 'loading') return;

			const src = window.pianoSwiperSrc;
			// Brak URL-a z Liquid — nic nie robimy, galerie zostają statyczne.
			if (!src) return;

			this.#state = 'loading';

			const script = document.createElement('script');
			script.src = src;
			script.defer = true;
			script.setAttribute(SWIPER_SCRIPT_ATTR, '');
			script.addEventListener('load', () => this.#flush(), { once: true });
			script.addEventListener('error', () => this.#fail(), { once: true });
			document.head.appendChild(script);
		}

		#flush() {
			this.#state = 'ready';
			const queue = this.#pending;
			this.#pending = [];
			for (const task of queue) task();
		}

		/**
		 * PN-057: wcześniej flaga „ładuję" nie wracała po błędzie sieci — Swiper nigdy się
		 * nie pojawiał, a KAŻDE kolejne zadanie lądowało w kolejce, której nikt już nie
		 * opróżniał. Teraz zapisujemy porażkę, czyścimy kolejkę i nie próbujemy w kółko.
		 */
		#fail() {
			this.#state = 'failed';
			this.#pending = [];
			console.error('[piano] Nie udało się pobrać Swipera — galerie kart zostaną statyczne.');
		}
	}

	/* ---------- Galerie kart ---------- */

	/**
	 * Leniwa inicjalizacja galerii przez JEDEN współdzielony IntersectionObserver.
	 * PN-036: init Swipera wymusza pobranie pierwszych zdjęć, więc robienie tego z góry
	 * dla wszystkich kart ściągało ~100 obrazków naraz. Wariant ukryty przez
	 * `display:none` nie ma boxa, więc nigdy nie zaintersectuje — i dobrze.
	 */
	class GalleryRegistry {
		#loader;
		/** @type {IntersectionObserver | null} */
		#observer = null;

		constructor(loader) {
			this.#loader = loader;
		}

		/** Bierze pod obserwację wszystkie jeszcze nieobsłużone galerie w zakresie. */
		scan(root) {
			eachElement(root, GALLERY_SELECTOR, (element) => {
				if (!markOnce(element, GALLERY_BOUND_ATTR)) return;

				const observer = this.#getObserver();
				if (observer) observer.observe(element);
				// Brak IntersectionObservera (bardzo stare przeglądarki) → inicjalizuj od razu.
				else this.initWhenReady(element);
			});
		}

		/** Inicjalizuje galerię, czekając w razie potrzeby na bundle Swipera. */
		initWhenReady(element) {
			this.#loader.whenReady(() => this.create(element));
		}

		/** Tworzy instancję Swipera natychmiast. Wymaga, by Swiper był już dostępny. */
		create(element) {
			if (!element || element.swiper || typeof Swiper === 'undefined') return;
			new Swiper(element, createGalleryOptions());
		}

		#getObserver() {
			if (this.#observer || !('IntersectionObserver' in window)) return this.#observer;

			this.#observer = new IntersectionObserver(
				(entries) => {
					for (const entry of entries) {
						if (!entry.isIntersecting) continue;
						this.#observer.unobserve(entry.target);
						this.initWhenReady(entry.target);
					}
				},
				{ rootMargin: GALLERY_ROOT_MARGIN }
			);
			return this.#observer;
		}
	}

	/* ---------- Fade-in zdjęć lazy ---------- */

	/**
	 * Jedno przejście po zakresie. Wcześniej ten kod siedział w karcie i odpalał się RAZ NA
	 * KARTĘ, za każdym razem przechodząc po CAŁYM dokumencie — czyli N kart × M zdjęć
	 * listenerów (na produkcji 144 × 1069 ≈ 152 tys.). To zawieszało wątek główny.
	 *
	 * Parę CSS↔JS trzymać razem: gasi `.product-item img[loading="lazy"]` w style.css,
	 * zapala ten kod. Rozjechanie zakresów = niewidoczne zdjęcia (patrz regresja kategorii).
	 */
	const LazyImages = {
		fadeIn(root) {
			eachElement(root, LAZY_IMAGE_SELECTOR, (image) => {
				if (!markOnce(image, FADE_BOUND_ATTR)) return;

				if (image.complete) {
					image.style.opacity = '1';
					return;
				}
				image.addEventListener(
					'load',
					() => {
						image.style.opacity = '1';
					},
					{ once: true }
				);
			});
		},
	};

	/* ---------- Wishlist (.wk-button wstrzykiwany przez aplikację) ----------
	   PN-057: nie ma tu już nic do zrobienia w JS. Utrzymywaliśmy `MutationObserver`
	   NA KAŻDY `.wk-button` tylko po to, by przepisać jego klasę `wk-selected` na
	   kontener. Żaden z tych obserwatorów nie był rozłączany, więc po podmianie
	   `innerHTML` na kolekcji odpięte przyciski zostawały retainerami — dokładnie
	   ten typ wycieku, który to zadanie usuwa. Zastąpione regułą CSS `:has()`
	   w `style.css` (`.wishlist-button-container:has(.wk-button.wk-selected)`):
	   reaktywna, bez stanu, obejmuje też przyciski wstrzyknięte później. */

	/* ---------- Fasada ---------- */

	class ProductItemApp {
		#galleries;

		constructor(galleries) {
			this.#galleries = galleries;
		}

		/** Jedyne wejście dla treści dorenderowanej AJAX-em. */
		init(root) {
			const scope = root || document;
			LazyImages.fadeIn(scope);
			this.#galleries.scan(scope);
		}
	}

	/* ---------- Montaż i publiczne API ---------- */

	const loader = new SwiperLoader();
	const galleries = new GalleryRegistry(loader);
	const app = new ProductItemApp(galleries);

	// Kształt tego obiektu jest ZAMROŻONY — woła go templates/collection.liquid.
	window.pianoProductItem = {
		init: (root) => app.init(root),
		initGallery: (element) => galleries.create(element),
	};

	// Kompatybilność wstecz (PN-057): sections/products-carousel.liquid (PN-036) woła ten
	// global, aby pre-inicjalizować bufor look-ahead galerii WEWNĄTRZ karuzeli — wspólny
	// IntersectionObserver nie widzi poza `overflow:hidden` karuzeli, więc bez tego klikanie
	// strzałek dawało "load flash". Wywołanie tam jest strzeżone przez
	// `typeof !== 'function'`, więc zmiana nazwy NIE rzuci błędem — po prostu cicho
	// wyłączy look-ahead. Nie zmieniać nazwy.
	// Przechodzi przez loader, więc działa też przed załadowaniem Swipera.
	window.__pianoInitGallery = (element) => galleries.initWhenReady(element);

	const boot = () => app.init(document);

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot, { once: true });
	} else {
		boot();
	}
})();
